import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, Input, output } from '@angular/core';

import { BehaviorSubject, Subject, take } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { User } from '@core/account-types';
import { filterBoolean } from '@core/common-legacy';
import type { Visibility, VisibilityLevel } from '@core/visibility-types';

import type { VisibilitySelectLabels } from './types';
import type { VisibilityInfoContext } from './visibility-selector-dropdown/visibility-selector-dropdown.component';
import { VisibilitySelectorAnalytics } from './visibility-selector.analytics';
import { VisibilitySelectorComponent } from './visibility-selector.component';
import { VisibilitySelectorService } from './visibility-selector.service';
import type { RoleWithAdmins } from '../../types';

@Component({
  selector: 'rt-visibility-selector-container',
  templateUrl: 'visibility-selector-container.component.html',
  styleUrls: ['./visibility-selector-container.component.less'],
  providers: [VisibilitySelectorAnalytics, VisibilitySelectorService],
  imports: [VisibilitySelectorComponent],
})
export class VisibilitySelectorContainerComponent implements OnChanges, OnInit, OnDestroy {
  @Input() public roleSelectionRequired: boolean = true;
  @Input() public hasOnlyMeLevel: boolean = true;
  @Input() public hasSelectTeamMembers: boolean = true;
  @Input() public hasLabel: boolean = true;
  @Input() public editable: boolean = true;
  @Input() public wrapOnSmallerResolutions: boolean = false;
  @Input() public visibility: Visibility;
  @Input() public avatarMaxLength: number = 3;
  @Input() public infoContext: VisibilityInfoContext = 'view';
  @Input() public buttonVariant: 'plain' | 'outline' = 'plain';
  @Input() public protectedUsersPosition: 'outside' | 'inside' = 'outside';
  @Input() public labels: VisibilitySelectLabels;
  @Input() public candidateId?: number;
  @Input() public offerId?: number;
  @Input() public groupUsersByAccess?: boolean;
  @Input() public disabledUserCopy?: string;
  @Input() public avatarSize: 'small' | 'small-x' = 'small';

  @Input() public dropdownPositions: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];
  @Input() public dropdownOffsetX: number = 0;
  @Input() public dropdownOffsetY: number = 0;

  public visibilityChanges = output<Visibility>();

  public allowAdminsChange: boolean = true;
  public admins: User[] = [];
  public rolesWithAdmins: RoleWithAdmins[] = [];

  private visibility$ = new BehaviorSubject<Visibility | null>(null);
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private analytics: VisibilitySelectorAnalytics,
    private visibilitySelectorService: VisibilitySelectorService,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.visibility) {
      this.visibility$.next(this.visibility);
    }
  }

  public ngOnInit(): void {
    this.visibilitySelectorService.featureVisibility$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(isEnabled => {
        this.allowAdminsChange = isEnabled;
        this.cdRef.detectChanges();
      });

    this.visibilitySelectorService
      .getVisibilityUsersData(this.visibility$.pipe(filterBoolean))
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(usersData => {
        this.admins = usersData.admins;
        this.rolesWithAdmins = usersData.rolesWithAdmins;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onVisibilityChanges(level: VisibilityLevel): void {
    this.visibilitySelectorService
      .onVisibilityChanges(level, this.visibility, {
        roleSelectionRequired: this.roleSelectionRequired,
        candidateId: this.candidateId,
        offerId: this.offerId,
        groupUsersByAccess: this.groupUsersByAccess,
        disabledUserCopy: this.disabledUserCopy,
      })
      .pipe(take(1))
      .subscribe(visibility => {
        if (!visibility) return;

        this.visibilityChanges.emit(visibility);
        this.cdRef.detectChanges();
      });
  }

  public onVisibilityDropdownToggle(open: boolean): void {
    if (open) {
      this.analytics.trackDropdownOpen();
    }
  }
}
